<template>
  <div>
    <div class="row">
      <div class="col-12">
        <h3 class="my-2">
          {{ $t('SETTINGS.TITLE.ACCOUNT_INFORMATION') }}
        </h3>
      </div>
    </div>
    <ValidationObserver
      ref="teacher-form"
      v-slot="{ invalid }"
      v-if="contactFormModel"
      @submit.prevent="updateTeacherInfo"
      tag="form"
    >
      <div class="row">
        <div class="col-12 col-md-6 pt-3">
          <label>
            {{ $t('SIGNUP.LABEL.YOUR_BIO_LINK') }}
          </label>
          <HMTextInput
            rules="required"
            autocomplete="organization"
            :placeholder="$t('SIGNUP.LABEL.YOUR_HANDLE')"
            v-model="studioUrl"
            name="handle"
          />
        </div>
        <div class="col-12 col-md-6 pt-3">
          <HMTextInput
            vid="studio_name"
            :label="$t('SETTINGS.LABEL.DISPLAY_NAME')"
            rules="required"
            v-model="contactFormModel.studio_name"
          />
        </div>
        <div class="col-12 col-md-6">
          <HMTextInput
            :label="$t('SETTINGS.LABEL.FIRST_NAME')"
            rules="required"
            v-model="contactFormModel.first_name"
          />
        </div>
        <div class="col-12 col-md-6">
          <HMTextInput
            :label="$t('SETTINGS.LABEL.LAST_NAME')"
            rules="required"
            v-model="contactFormModel.last_name"
          />
        </div>
        <div class="col-12 d-flex justify-content-end">
          <HMButton
            color="brand"
            type="submit"
            :disabled="invalid || inProgress"
            :loading="inProgress"
          >
            {{ $t('GENERAL.BUTTON.SAVE') }}
          </HMButton>
        </div>
      </div>
    </ValidationObserver>
    <hr class="mt-5 mb-5 w-100" />
    <ValidationObserver tag="div">
      <form @submit.prevent="updatePassword">
        <div class="row">
          <div class="col-12 pb-3">
            <h3 class="my-2">
              {{ $t('SETTINGS.TITLE.CHANGE_PASSWORD') }}
            </h3>
          </div>
          <div class="col-12 col-md-6">
            <HMTextInput
              :label="$t('SETTINGS.LABEL.NEW_PASSWORD_1')"
              v-model="password.newPassword1"
              type="password"
            />
          </div>
          <div class="col-12 col-md-6">
            <HMTextInput
              :label="$t('SETTINGS.LABEL.NEW_PASSWORD_2')"
              v-model="password.newPassword2"
              type="password"
            />
          </div>
          <div class="invalid-feedback d-block" v-show="formError">
            {{ formError }}
          </div>
          <div class="col-12 d-flex justify-content-end">
            <HMButton class="btn-primary" :loading="inProgress" type="submit">
              {{ $t('SETTINGS.TITLE.CHANGE_PASSWORD') }}
            </HMButton>
          </div>
        </div>
      </form>
    </ValidationObserver>
    <hr class="mt-5 mb-5 w-100" />
    <h2 class="mb-4">
      {{ $t('SETTINGS.TITLE.INTEGRATIONS') }}
    </h2>
    <StripeIntegration :returnUrl="stripeReturnUrl" id="stripe" />
    <hr class="mt-5 mb-5 w-100" />
    <h2 class="mb-4">
      {{ $t('SETTINGS.TITLE.TIP_JAR') }}
    </h2>
    <TipJarIntegration
      :showTipJarPage="false"
      disabledTitle="SETTINGS.TEXT.ENABLE_STRIPE_FOR_TIP_JAR_IN_BIO"
    >
      <template v-slot:description v-if="isBio">
        {{ $t('BIO.TEXT.TIP_JAR_EXPLANATION_1') }}
        <router-link :to="{ name: 'bio-upgrade' }">
          {{ $t('BIO.TEXT.TIP_JAR_EXPLANATION_2') }}
        </router-link>
        {{ $t('BIO.TEXT.TIP_JAR_EXPLANATION_3') }}
      </template>
    </TipJarIntegration>
    <hr class="mt-5 mb-5 w-100" />
    <h2 class="mb-4">
      {{ $t('SETTINGS.TITLE.TRACKING') }}
    </h2>
    <FacebookPixelIntegration />
    <hr class="my-4 w-100" />
    <GoogleAnalyticsIntegration />
  </div>
</template>

<script>
import Vue from 'vue';
import { ValidationObserver } from 'vee-validate';
import { mapGetters } from 'vuex';
import { errorToMessage } from '@/core/utils/errors';
import HMButton from '@/web/components/common/HMButton.vue';
import StripeIntegration from '@/web/components/settings/StripeIntegration.vue';
import TipJarIntegration from '@/web/components/settings/TipJarIntegration.vue';
import FacebookPixelIntegration from '@/web/components/settings/FacebookPixelIntegration.vue';
import GoogleAnalyticsIntegration from '@/web/components/settings/GoogleAnalyticsIntegration.vue';
import { pick } from 'lodash';

export default Vue.extend({
  components: {
    ValidationObserver,
    HMButton,
    StripeIntegration,
    TipJarIntegration,
    FacebookPixelIntegration,
    GoogleAnalyticsIntegration,
  },
  computed: {
    ...mapGetters('user', ['teacher']),
    stripeReturnUrl() {
      return window.location.href;
    },
    studioUrl: {
      get() {
        return this.contactFormModel.studio_url || '';
      },
      set(studio_url) {
        this.$set(
          this.contactFormModel,
          'studio_url',
          this.slugify(studio_url)
        );
      },
    },
    isBio() {
      return this.teacher?.is_bio;
    },
  },
  data() {
    return {
      contactFormModel: null,
      inProgress: false,
      password: {
        newPassword1: '',
        newPassword2: '',
      },
      formError: null,
    };
  },
  methods: {
    slugify(s) {
      return `${s.trim().replace(/[^0-9a-z-]/gi, '')}`;
    },
    async updateTeacherInfo() {
      this.inProgress = true;
      try {
        const data = { teacher: this.contactFormModel };
        const dispatchPromise = await this.$store.dispatch(
          'user/updateTeacher',
          data
        );
        await this.$store.dispatch('setting/getAccountInfo');
        this.$emit('close');
        this.$emit('onSubmit', dispatchPromise);
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('SETTINGS.NOTIFICATIONS.CONTACT_INFORMATION_SUCCESS'),
        });
      } catch (error) {
        console.log('error', error?.response);
        if (error?.response?.data?.teacher?.studio_url) {
          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Error saving contact information',
            text: 'Invalid handle',
            duration: -1, // stay ON
          });
        } else {
          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Error saving contact information',
            text: errorToMessage(error?.response, `Request failed: ${error}`),
            duration: -1, // stay ON
          });
        }
      }
      this.inProgress = false;
    },
    teacherToFormModel(teacher) {
      return pick(teacher, [
        'first_name',
        'last_name',
        'studio_name',
        'studio_url',
        'web_link',
        'address',
        'city',
        'state',
        'country',
        'zipcode',
        'phone',
        'email',
      ]);
    },
  },
  watch: {
    teacher: {
      immediate: true,
      handler() {
        this.contactFormModel = this.teacherToFormModel(this.teacher);
        console.log(this.contactFormModel);
      },
    },
  },
});
</script>

<style lang="scss" scoped></style>
